import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from './Dashboard';
import { Database, FunnelSimple, TrendUp, Tag } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LibraryPage = ({ user }) => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const loadTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/templates`);
      setTemplates(response.data);
    } catch {
      // Templates unavailable
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const categories = [
    { value: 'all', label: t('library.cat_all') },
    { value: 'high_ticket', label: t('library.cat_high_ticket') },
    { value: 'low_ticket', label: t('library.cat_low_ticket') },
    { value: 'lead_gen', label: t('library.cat_lead_gen') },
  ];

  const filteredTemplates = selectedCategory === 'all'
    ? templates
    : templates.filter(t => t.category === selectedCategory);

  const getCategoryColor = (category) => {
    const colors = {
      high_ticket: 'text-[#3B82F6] bg-[#3B82F6]/20',
      low_ticket: 'text-[#10B981] bg-[#10B981]/20',
      lead_gen: 'text-[#F59E0B] bg-[#F59E0B]/20',
    };
    return colors[category] || 'text-white/60 bg-white/10';
  };

  return (
    <DashboardLayout user={user} activePage="/library">
      <div className="p-6" data-testid="library-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-[#2563EB]/20 rounded-xl flex items-center justify-center">
              <Database size={28} weight="bold" className="text-[#2563EB]" />
            </div>
            <h1 className="text-4xl font-[Outfit] font-bold tracking-tight">{t('library.title')}</h1>
          </div>
          <p className="text-white/60">{t('library.subtitle')}</p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat.value}
              data-testid={`category-${cat.value}`}
              onClick={() => setSelectedCategory(cat.value)}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                selectedCategory === cat.value
                  ? 'bg-[#2563EB] text-white'
                  : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-16 h-16 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <div
                key={template.template_id}
                data-testid={`template-${template.template_id}`}
                className="p-6 bg-[#121212] border border-white/10 rounded-xl hover:-translate-y-1 hover:border-[#2563EB]/50 transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-[#2563EB]/20 rounded-lg flex items-center justify-center">
                    <FunnelSimple size={24} weight="bold" className="text-[#2563EB]" />
                  </div>
                  {template.conversion_rate && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-[#10B981]/20 rounded-full">
                      <TrendUp size={14} weight="bold" className="text-[#10B981]" />
                      <span className="text-xs text-[#10B981] font-medium">
                        {template.conversion_rate}%
                      </span>
                    </div>
                  )}
                </div>

                <h3 className="text-xl font-[Outfit] font-bold mb-2">{template.title}</h3>
                <p className="text-white/60 text-sm mb-4 line-clamp-2">{template.description}</p>

                <div className="flex items-center gap-2 mb-4">
                  <Tag size={16} weight="bold" className="text-white/40" />
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(template.category)}`}>
                    {categories.find(c => c.value === template.category)?.label || template.category}
                  </span>
                </div>

                {template.structure?.pages && (
                  <div>
                    <p className="text-xs text-white/40 uppercase tracking-[0.2em] mb-2">{t('library.structure')}</p>
                    <div className="flex flex-wrap gap-1">
                      {template.structure.pages.map((page, idx) => (
                        <span
                          key={`${template.template_id}-page-${idx}-${page}`}
                          className="px-2 py-1 bg-[#0A0A0A] border border-white/10 rounded text-xs text-white/60"
                        >
                          {page}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && filteredTemplates.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-[#2563EB]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Database size={40} weight="bold" className="text-[#2563EB]" />
            </div>
            <h3 className="text-2xl font-[Outfit] font-bold mb-3">{t('library.empty_title')}</h3>
            <p className="text-white/60">{t('library.empty_sub')}</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LibraryPage;